/**
 * The Control Center core and the account-profile workspaces: navigation,
 * organization, personal profile and settings, clinic settings, terminology,
 * clinical protocols and print forms.
 *
 * One module owns both workspaces because the profile forms reuse the same
 * compact administrative form language while keeping account and clinic
 * routes, data and capabilities separate.
 * `nav.controlCenter` stays in `common`: the navigation entry belongs to the
 * navigation. Access administration lives in `controlCenterAccess`, and the
 * external-service sections in `controlCenterIntegrations`.
 *
 * The print-form copy is deliberately concrete about where each fact comes
 * from. This section reads a registry Terysling owns, while the editor it links
 * to is a separate service; a reader who cannot tell those apart cannot tell a
 * missing release from an editor that is down, and those want opposite actions.
 *
 * Composed into the public catalogue by `catalogue.en.ts`. Nothing imports this
 * module directly: consumers ask the catalogue, so how the messages are stored
 * stays this layer's business.
 */
export const controlCenter = {
  "controlCenter.administration.scopeSharedProfiles": "Shared terminology profiles",
  "controlCenter.administration.platformAI": "AI services and tools",
  "controlCenter.administration.platformPrintForms": "Print form tools",
  "controlCenter.administration.platformTerminology": "Terminology configuration",
  "controlCenter.administration.platformQuotas": "Clinic limits",
  "controlCenter.administration.platform": "Platform administration",
  "controlCenter.administration.platformDescription": "Shared resources and technical tools",
  "controlCenter.administration.organizationGroup": "Organization",
  "controlCenter.administration.teamGroup": "Staff and access",
  "controlCenter.administration.formsGroup": "Forms and dictionaries",
  "controlCenter.administration.connectionsGroup": "Connections and AI",
  "controlCenter.administration.registration": "Patient registration",
  "controlCenter.administration.registrationDescription": "Questionnaire, reviewer and patient sign-in",
  "controlCenter.administration.scopePlatform": "Applies to the whole platform",
  "controlCenter.administration.scopeClinic": "Clinic",
  "controlCenter.administration.scopePersonalClinic": "Your settings in the clinic",
  "controlCenter.administration.scopeAccount": "Your account",
  "controlCenter.administration.breadcrumb": "Location",
  "controlCenter.administration.sharedTerminology": "These profiles can be used by several clinics. Editing a shared profile changes its dictionaries for every clinic using it.",
  "controlCenter.administration.aiTools": "AI tools",
  "controlCenter.administration.aiToolsDescription": "Document tester and technical diagnostics",
  "controlCenter.administration.security": "Sign-in and security",
  "controlCenter.administration.securityDescription": "Username, password and verified account email",
  "controlCenter.administration.communications": "Notifications and communication",
  "controlCenter.administration.communicationsDescription": "Messengers, notification hours and message signature",

  'onboardingSettings.title': 'Patient onboarding',
  'onboardingSettings.loading': 'Loading onboarding settings',
  'onboardingSettings.loadFailed': 'Could not load onboarding settings',
  'onboardingSettings.saveFailed': 'Could not save onboarding settings.',
  'onboardingSettings.invalid': 'The questionnaire or reviewing clinician is no longer available in this organization.',
  'onboardingSettings.unsavedIntent': 'Patient onboarding settings',
  'onboardingSettings.protocol': 'Questionnaire',
  'onboardingSettings.reviewer': 'Reviewing clinician',
  'onboardingSettings.unassigned': 'Unassigned; notify the owner',
  'onboardingSettings.chooseProtocol': 'Select a questionnaire',
  'onboardingSettings.unavailable': 'Unavailable',
  "controlCenter.history.title": "What’s new",
  'controlCenter.history.description': "Tasks included in published releases. Changes without a task number are not listed.",
  'controlCenter.history.loading': "Loading update history…",
  'controlCenter.history.empty': "No published updates have been recorded yet.",
  'controlCenter.history.noTasks': "This release has no recorded tasks.",
  'controlCenter.history.more': "Earlier updates",
  'controlCenter.history.unavailable': "Update history is unavailable.",

  "controlCenter.title": "Clinic management",
  "controlCenter.subtitle": "Configuration of the selected clinic.",
  "controlCenter.navLabel": "Clinic management sections",
  "controlCenter.nav.organization": "Clinic details",
  "controlCenter.nav.organizationDescription": "Operating legal entity and clinic details",
  "controlCenter.nav.uploads": "Imaging import queue",
  'controlCenter.nav.uploadsDescription': 'Unfinished imaging transfers and imports',
  "controlCenter.nav.users": "Staff",
  'controlCenter.nav.usersDescription': 'Accounts, roles and access',
  "controlCenter.nav.workplaces": "Work in branches",
  'controlCenter.nav.workplacesDescription': 'Clinician branches, professions and specialties',
  "controlCenter.nav.roles": "Roles and permissions",
  'controlCenter.nav.rolesDescription': 'Role bundles and the capabilities they grant',
  "controlCenter.nav.settings": "Time, calendar and formats",
  "controlCenter.nav.settingsDescription": "Clinic time zone, working calendar and display formats",
  "controlCenter.nav.terminology": "Dictionaries and classifications",
  'controlCenter.nav.terminologyDescription': 'Dictionaries and versions per section',
  "controlCenter.nav.modules": "Clinic modules",
  'controlCenter.nav.modulesDescription': 'Embedded modules: on, off or hidden',
  "controlCenter.nav.connectors": "Message delivery channels",
  'controlCenter.nav.connectorsDescription': 'Email, Telegram and WhatsApp connectors and their credentials',
  'controlCenter.nav.printForms': 'Print forms',
  'controlCenter.nav.printFormsDescription': 'Registered forms and editor',
  "controlCenter.nav.protocols": "Record forms and questionnaires",
  "controlCenter.nav.protocolsDescription": "Definitions, versions and selection rules",
  "controlCenter.nav.ai": "AI functions",
  'controlCenter.nav.aiDescription': 'Backends, prompt registry and support files',
  'controlCenter.discard': 'Discard changes',

  "controlCenter.uploads.title": "Imaging import queue",
  'controlCenter.uploads.subtitle':
    'Every unfinished imaging import in this clinic, including paused transfers and studies awaiting confirmation.',
  'controlCenter.uploads.loading': 'Loading uploads',
  'controlCenter.uploads.failed': 'Uploads could not be loaded. No row was intentionally hidden.',
  'controlCenter.uploads.forbidden': 'You may not read the imaging uploads for this clinic',
  'controlCenter.uploads.tableLabel': 'Unfinished imaging uploads',
  'controlCenter.uploads.empty': 'No unfinished uploads',
  'controlCenter.uploads.emptyDetail': 'New imaging imports appear here as soon as Core accepts them.',
  'controlCenter.uploads.noMatches': 'No uploads match these filters',
  'controlCenter.uploads.patient': 'Patient',
  'controlCenter.uploads.source': 'Source',
  'controlCenter.uploads.uploader': 'Added by',
  'controlCenter.uploads.updated': 'Updated',
  'controlCenter.uploads.openPatientFiles': 'Open patient files',
  'controlCenter.uploads.cancel': 'Cancel and delete',

  "organization.title": "Clinic details",
  'branches.title': 'Branches',
  'branches.name': 'Name',
  'branches.address': 'Address',
  'branches.default': 'Default branch',
  'branches.active': 'Active',
  'branches.archived': 'Archived',
  'branches.create': 'New branch',
  'branches.save': 'Save branch',
  'branches.archive': 'Archive branch',
  'branches.archiveConfirm': 'Archive this branch? Recorded visits will be retained.',
  'branches.empty': 'No branches',
  'branches.loadFailed': 'Could not load branches.',
  'branches.failed': 'Could not save the branch. Your entries are retained.',
  'branches.forbidden': 'You may not change branches.',
  'branches.invalid': 'Check the branch name and address.',
  'branches.changed': 'The branch changed while this editor was open. Reload its current revision.',
  'branches.defaultRequired': 'Select another active default branch first.',
  'branches.inUse': 'This branch has future appointments, active resources or unfinished visits.',
  'branches.reload': 'Reload branch',
  'branches.reloaded': 'Current branch details loaded. Review them before saving.',
  'branches.unsaved': 'Unsaved branch details',
  'branches.subtitle': 'The places where this clinic sees patients.',
  'organization.subtitle': 'The legal identity operating this clinic, with its saved revisions.',
  'organization.loading': 'Loading the organization',
  'organization.loadFailed': 'Could not load the organization',
  'organization.forbidden': 'You may not read organization details for this clinic',
  'organization.name': 'Organization name',
  'organization.legalName': 'Legal name',
  'organization.country': 'Country',
  'organization.addressLine1': 'Address',
  'organization.addressLine2': 'Address details',
  'organization.city': 'City',
  'organization.postalCode': 'Postal code',
  'organization.phone': 'Phone',
  'organization.email': 'Email',
  'organization.website': 'Website',
  'organization.taxType': 'Tax identifier type',
  'organization.taxNone': 'Not set',
  'organization.taxIlCompany': 'Israeli company number',
  'organization.taxIlVat': 'Israeli VAT number',
  'organization.taxIlIdentity': 'Israeli identity number',
  'organization.taxOther': 'Other identifier',
  'organization.taxValue': 'Tax identifier',
  'organization.identityLeaf': 'Identity',
  'organization.addressLeaf': 'Address',
  'organization.contactsLeaf': 'Contacts',
  'organization.validityLeaf': 'Validity',
  'organization.save': 'Save organization',
  'organization.saving': 'Saving',
  'organization.saved': 'Organization saved',
  'organization.unsavedIntent': 'Organization',
  'organization.conflict': 'Someone else changed the organization while this form was open.',
  'organization.saveFailed': 'Could not save the organization.',

  "profile.workspaceTitle": "My profile",
  'profile.workspaceSubtitle': 'Your personal information and account settings.',
  'profile.navLabel': 'Profile sections',
  // A TILE LABEL IS A WORD, NOT THE SECTION'S HEADING. `.nav-tile__title` is
  // one line with no wrap, and the panel's own width leaves it about 138px:
  // 'Personal information' overran that in every system font measured and
  // shipped as 'Personal informati…'. The heading inside the page keeps the
  // full name; the description carries the sentence, clamped to two lines.
  "profile.nav.personal": "Personal details",
  "profile.nav.personalDescription": "Names, contacts and work in the clinic",
  "profile.nav.tasks": "Mentions of me",
  'profile.nav.tasksDescription': 'Tasks that mention you',
  "profile.nav.settings": "Personal AI access",
  'profile.nav.settingsDescription': 'Your personal AI access',
  "profile.group.account": "Account",
  "profile.group.connections": "Connections and AI",
  "profile.group.development": "Development",
  "profile.nav.designDrafts": "Design drafts",
  "profile.nav.designDraftsDescription": "Interface proposals saved by an assistant",
  'profile.tasksEmpty': 'No tasks mention you',
  'profile.tasksEmptyDetail': 'Tasks whose message names you as a recipient appear here.',
  'profile.personalTitle': 'Personal information',
  "profile.personalSubtitle": "Your names, contacts and work in the selected clinic.",
  // The MoH classification, which is a PRACTITIONER's and not a person's — it
  // is why this card carries a leaf a Patient chart does not.
  'profile.professionalTitle': 'Clinical classification',
  'profile.loading': 'Loading your profile',
  'profile.loadFailed': 'Could not load your profile',
  'profile.unsavedIntent': 'Profile',
  'profile.messageSignature': 'Message signature',
  'profile.messageSignatureHint': 'Prepared once and appended to every message you send to a patient.',
  'profile.passwordTitle': 'Password',
  'profile.passwordEnrollTitle': 'Set a password',
  'profile.passwordEnrollmentUnsaved': 'First password',
  'profile.passwordOpenTelegram': 'Confirm in Telegram',
  'profile.passwordTelegramFresh': 'Telegram confirmation has expired or does not match this account. Reopen the staff Mini App.',
  'profile.passwordTelegramUnavailable': 'Telegram confirmation is unavailable.',
  'profile.passwordAlreadySet': 'This account already has a password.',
  'profile.passwordEnrollmentThrottled': 'Too many attempts. Try again in 15 minutes.',
  'profile.passwordEnrollmentRetry': 'Retry',
  'profile.passwordRequired': 'A password change is required before continuing.',
  'profile.changePassword': 'Change password',
  'profile.currentPassword': 'Current password',
  'profile.newPassword': 'New password',
  'profile.passwordSave': 'Save password',
  'profile.passwordSaving': 'Saving',
  'profile.passwordSaved': 'Password saved',
  'profile.passwordWrong': 'The current password is not correct.',
  'profile.passwordFailed': 'Could not save the password.',
  'profile.passwordUnsavedIntent': 'Password change \u2014 not yet applied',

  "profileSettings.title": "Personal AI access",
  "profileSettings.subtitle": "Your OpenAI key for the selected clinic. It is not shared with your other clinic memberships.",
  'profileSettings.openAISecretTitle': 'OpenAI key',
  'profileSettings.secretUnsavedIntent': 'OpenAI key',
  'profileSettings.secretLoading': 'Loading OpenAI key status',
  'profileSettings.secretUnavailable': 'Secret storage is not configured.',
  'profileSettings.secretLoadFailed': 'Could not load OpenAI key status.',
  'profileSettings.key': 'Key',
  'profileSettings.secretSet': 'Set key',
  'profileSettings.secretChange': 'Replace key',
  'profileSettings.openAISecret': 'OpenAI API key',
  'profileSettings.secretSave': 'Save key',
  'profileSettings.secretSaving': 'Saving',
  'profileSettings.secretClear': 'Clear key',
  'profileSettings.secretClearing': 'Clearing',
  'profileSettings.secretSaved': 'OpenAI key saved',
  'profileSettings.secretCleared': 'OpenAI key cleared',
  'profileSettings.secretSaveFailed': 'Could not save the OpenAI key.',
  'profileSettings.secretClearFailed': 'Could not clear the OpenAI key.',

  "clinicSettings.title": "Time, calendar and formats",
  'clinicSettings.subtitle': 'Clinic-wide civil time and date, time and number display.',
  'clinicSettings.loading': 'Loading clinic settings',
  'clinicSettings.loadFailed': 'Could not load clinic settings',
  'clinicSettings.forbidden': 'You may not read settings for this clinic',
  'clinicSettings.unsavedIntent': 'Clinic settings',
  'clinicSettings.timeZone': 'Clinic time zone',
  'clinicSettings.workingCalendar': 'Clinic working calendar',
  'clinicSettings.dateFormat': 'Date format',
  'clinicSettings.dateFormat.dayMonthYear': '23/08/2026',
  'clinicSettings.dateFormat.monthDayYear': '08/23/2026',
  'clinicSettings.dateFormat.yearMonthDay': '2026-08-23',
  'clinicSettings.timeFormat': 'Time format',
  'clinicSettings.timeFormat.24Hour': '24-hour (14:30)',
  'clinicSettings.timeFormat.12Hour': '12-hour (2:30 PM)',
  'clinicSettings.numberFormat': 'Number format',
  'clinicSettings.numberFormat.decimalDotGroupComma': '1,234.56',
  'clinicSettings.numberFormat.decimalCommaGroupSpace': '1 234,56',
  'clinicSettings.numberFormat.decimalCommaGroupDot': '1.234,56',
  'clinicSettings.saveFailed': 'Could not save clinic settings.',

  // The two leaves the record is read in. The zone is not a display choice: it
  // is the civil day the server resolves for this clinic, and everything below
  // it is only how a resolved value is printed.
  'clinicSettings.civilTime': 'Civil time and working calendar',
  'clinicSettings.displayFormats': 'Display formats',

  // The second document on this page: what the clinic holds credentials for.
  'clinicSettings.services': 'Connected services',
  'clinicSettings.servicesSubtitle':
    'Outside services this clinic holds a credential for. A saved secret is never shown again — only its first and last characters, so the stored one can be recognized.',

  // Said of ANY stored secret, on this page and on the account's own settings:
  // one prefix, because one component prints both and a per-surface id would
  // drift the moment either surface changed its wording.

  'terminology.profile': "Profile",
  'terminology.profileSetupRequired': 'Terminology profile is not configured. Developer setup is required.',
  'terminology.profileSelected': "Selected profile",
  'terminology.profileSelect': "Use for this clinic",
  'terminology.profileEdit': "Edit profile",
  'terminology.profileCopy': "Create a copy",
  'terminology.profileDelete': "Delete unused profile",
  'terminology.profileName': "Name",
  'terminology.profileCountry': "Country (ISO code)",
  'terminology.profileShared': "Changes affect every clinic using this profile.",
  'terminology.profileUnsaved': "Unsaved terminology profile",
  'terminology.profileConflict': "The profile or clinic selection has changed. Reload the stored revision before saving.",
  'terminology.profileReload': "Reload and discard profile edits",
  'terminology.profileReloadFailed': "Could not reload. Your draft is retained.",
  'terminology.profileReloaded': "Stored profile and clinic selection loaded. Review before saving.",
  'terminology.profileFailed': "Could not save. Check country, referenced clinics and the latest revision; your draft is retained.",
  'terminology.profileCreate': "Create profile",
  'terminology.profileUnbind': "Remove binding",
  "terminology.title": "Dictionaries and classifications",
  'terminology.subtitle': 'Which dictionary each part of the product reads, and the version it reads it at.',
  'terminology.loading': 'Loading the terminology bindings',
  'terminology.loadFailed': 'Could not load the terminology bindings',
  'terminology.forbidden': 'You may not read the terminology bindings for this clinic',
  // The bindings are on screen and the catalogue is not, which is a different
  // failure from "nothing loaded" and wants a different sentence.
  'terminology.serviceUnavailable':
    'The terminology service is not answering. The bindings below are what this clinic has stored; no dictionary or version can be chosen until it is back.',

  'terminology.openApiReference': 'Open the terminology API reference',
  'terminology.openApiReferenceNote':
    'The reference opens in a new tab. It documents the terminology service itself, not this registry.',

  'terminology.section': 'Section',
  'terminology.sectionDiagnoses': 'Diagnoses',
  'terminology.sectionOncologyTopography': 'Oncology topography',
  'terminology.sectionOncologyTNM': 'Oncology TNM',
  'terminology.sectionOncologyMorphology': 'Oncology morphology',
  'terminology.sectionLaboratory': 'Laboratory',
  'terminology.sectionServices': 'Medical services',
  'terminology.sectionPractitionerProfessions': 'Practitioner professions',
  'terminology.sectionOrganizationStructure': 'Organization structure',
  'terminology.sectionAllergyDrugSubstances': 'Anamnesis: drug allergens',
  'terminology.sectionAllergyOtherSubstances': 'Anamnesis: other allergens',
  'terminology.sectionAllergyManifestations': 'Anamnesis: allergic reactions',
  'terminology.sectionHabits': 'Anamnesis: habits',

  'terminology.dictionary': 'Dictionary',
  'terminology.dictionaryFor': 'Dictionary for {section}',
  'terminology.version': 'Version',
  'terminology.versionFor': 'Version for {section}',
  // NOT "none" and not an empty box: following the published current version is
  // a decision the clinic stores, and it is the one most sections are on.
  'terminology.versionFollows': 'Follow current',
  'terminology.versionUnpublished': '{version} (no longer published)',
  'terminology.versionsLoading': 'Loading versions',
  'terminology.versionsFailed': 'Could not load the versions of this dictionary',

  'terminology.resolvedVersion': 'In force',

  /*
   * A section the product already reads, against one it only records. The two
   * look identical in a table of bindings, and an operator who cannot tell them
   * apart will believe a row they changed altered clinical behaviour.
   */
  'terminology.status': 'Status',
  'terminology.statusActive': 'In use',
  'terminology.statusRecorded': 'Recorded only',

  'terminology.updated': 'Changed',

  'terminology.actions': 'Actions',
  'terminology.editNamed': 'Edit {name}',
  'terminology.save': 'Save',
  'terminology.saving': 'Saving',
  'terminology.cancel': 'Cancel',
  'terminology.reload': 'Read the registry again',
  'terminology.unavailableEdit': 'The terminology service is not answering',

  'terminology.saveFailed': 'Could not save the binding',
  'terminology.saveForbidden': 'You may not change the terminology bindings for this clinic',
  'terminology.saveConflict':
    'Someone else changed this binding. Read the registry again and decide against what is stored now.',
  'terminology.saveRejected': 'That dictionary and version cannot be bound to this section',

  'terminology.tableLabel': 'Terminology bindings',
  'terminology.empty': 'No product section consumes terminology',
  'terminology.emptyDetail': 'The section list comes from the product, not from this clinic.',
  'terminology.noMatches': 'No section matches the filters',
  'terminology.noMatchesDetail': 'Clear the filters to see every section.',

  "protocols.title": "Record forms and questionnaires",
  'protocols.subtitle': 'Published definitions, activation and deterministic assignment for this clinic.',
  'protocols.loading': 'Loading protocols and encounter types',
  'protocols.loadFailed': 'Could not load the protocol registry',
  'protocols.forbidden': 'You may not read protocols for this clinic',
  'protocols.openEditor': 'Open protocol editor',
  'protocols.openEditorHint': 'The editor opens in a new tab with this account.',
  'protocols.releasesTitle': 'Registered releases',
  'protocols.releasesEmpty': 'No MPE release is registered for this clinic.',
  'protocols.protocol': 'Protocol',
  'protocols.release': 'Release',
  'protocols.registered': 'Registered',
  'protocols.state': 'State',
  'protocols.actions': 'Actions',
  'protocols.active': 'Active',
  'protocols.inactive': 'Inactive',
  'protocols.activate': 'Activate release',
  'protocols.deactivate': 'Remove from selection',
  'protocols.policyTitle': 'Selection policy',
  'protocols.policyHint':
    'An exact workplace specialty and stable encounter type rule wins. The clinic default is used only when no exact rule matches.',
  'protocols.defaultLabel': 'Clinic default',
  'protocols.chooseProtocol': 'Choose an active protocol',
  'protocols.saveDefault': 'Save default',
  'protocols.expertise': 'Specialty',
  'protocols.visitType': 'Encounter type',
  'protocols.chooseVisitType': 'Choose an encounter type',
  'protocols.addRule': 'Add exact rule',
  'protocols.rulesEmpty': 'No exact assignment rules are configured.',
  'protocols.deleteRuleNamed': 'Delete the assignment rule for {name}',
  // The same delete, over the ticked rows. A rule carries no version and no
  // dependency on its neighbours, so a refusal on one says nothing about the
  // next: the run walks on and names what it could not take. Deleting several
  // at once is asked first, which deleting one is not — a single bin press is
  // one rule an operator is looking straight at.
  'protocols.deleteSelected': 'Delete {count} selected',
  'protocols.deleteSelectedTitle': 'Delete the selected assignment rules?',
  'protocols.deleteSelectedDescription':
    '{count} rules will stop assigning a protocol. Encounters already started keep the protocol they were given.',
  'protocols.deleteSelectedConfirm': 'Delete {count} rules',
  'protocols.deleteSelectedCancel': 'Keep the selection',
  'protocols.rulesDeletedCount': '{count} assignment rules were deleted.',
  'protocols.rulesDeletedBefore': '{count} rules were deleted.',
  'protocols.actionFailed': 'The command could not be completed.',
  'protocols.activateFailed': 'The release could not be activated.',
  'protocols.defaultFailed': 'The clinic default could not be changed.',
  'protocols.createRuleFailed': 'The rule could not be added.',
  'protocols.deleteRuleFailed': 'The rule could not be deleted.',
  'protocols.activateRequiresPrimary':
    'A protocol the selection policy uses must stay a primary encounter document.',
  'protocols.defaultRequiresPrimary':
    'The clinic default must be an active primary encounter document.',
  'protocols.releaseGone': 'That release is no longer registered. Reload the registry.',
  'protocols.deactivateFailed': 'The protocol could not be removed from selection.',
  'protocols.deactivateUsedByRule':
    'An assignment rule still names this protocol. Delete the rule first.',
  'protocols.deactivateIsDefault':
    'This protocol is the clinic default. Choose another default first.',
  'protocols.activationGone': 'This protocol is no longer active. Reload the registry.',
  'protocols.ruleGone': 'That rule is already gone. Reload the registry.',
  'protocols.ruleExists': 'A rule for this expertise and encounter type already exists.',
  'protocols.ruleRejected':
    'The rule was rejected. Check the expertise, the encounter type and the protocol.',
  'protocols.registryUnavailable': 'The protocol registry is unavailable. Try again shortly.',
  'protocols.suggestionStale': 'The saved name changed. Reopen the type and request new suggestions.',
  'protocols.unsavedIntent': 'Scheduling form \u2014 entries that were never saved',
  'protocols.visitTypesTitle': 'Encounter types',
  'protocols.visitTypesHint':
    'The stable type id is used by protocol rules. Published versions preserve duration and display history.',
  'protocols.visitTypesForbidden': 'You may not read encounter types for this clinic',
  'protocols.visitTypesEmpty': 'No encounter types are registered.',
  'protocols.visitTypeCode': 'Stable code',
  'protocols.visitTypeName': 'Display name',
  'protocols.duration': 'Duration, minutes',
  'protocols.requirementCode': 'Requirement code',
  'protocols.capabilityCode': 'Capability code',
  'protocols.requirementQuantity': 'Quantity',
  'protocols.addRequirement': 'Add resource requirement',
  'protocols.removeRequirement': 'Remove',
  'protocols.createVisitType': 'Create encounter type',
  'protocols.versions': 'Published versions',
  'protocols.versionSummary': 'v{version} · {minutes} min',
  'protocols.publishVersion': 'Publish version',
  'protocols.rulesTableLabel': 'Exact assignment rules',
  // The context-panel forms: what the Add buttons and the per-row publish
  // action open beside the registry, on the shared panel chrome.
  'protocols.newRuleTitle': 'New assignment rule',
  'protocols.newVisitTypeTitle': 'New encounter type',
  'protocols.publishVersionTitle': 'Publish a version',

  'printForm.title': 'Print forms',
  'printForm.subtitle': 'The printable forms this clinic has registered, and the release each one prints from.',

  'printForm.loading': 'Loading the print forms',
  'printForm.loadFailed': 'Could not load the print forms',
  'printForm.forbidden': 'You may not read the print forms for this clinic',
  'printForm.bindingsEmpty': 'This clinic has no print form registered yet',

  'printForm.formsTableLabel': 'Registered print forms',
  'printForm.form': 'Form',
  'printForm.bindingCode': 'Form code',
  'printForm.contextSchema': 'Data schema',
  'printForm.anchorTypes': 'Printed from',
  'printForm.bindingStatus': 'Form status',
  'printForm.bindingActive': 'In use',
  // Not "disabled": the form still exists and its releases are still stored.
  // What changed is that this clinic no longer prints from it.
  'printForm.bindingInactive': 'Withdrawn',
  'printForm.actions': 'Actions',
  'printForm.detailsNamed': 'Details for {name}',
  'printForm.detailsTitle': 'Form details',

  'printForm.currentRelease': 'Current release',

  'printForm.currentReleaseNamed': 'Current release {version}',
  'printForm.noCurrentRelease': 'No release has been activated for this form',
  // The registry cell's short form of the sentence above: the column is named
  // "Release", so the cell states only that there is none to name.
  'printForm.releaseAbsent': 'Not activated',
  'printForm.releaseReadFailed': 'Could not read the current release for this form',
  // The registry cell's short form of the sentence above, in the shape
  // `printForm.releaseAbsent` uses: the column is named "Release", so the cell
  // states only that this one is not known — never that there is none.
  'printForm.releaseUnread': 'Not read',
  'printForm.releaseVersion': 'Release',
  'printForm.pointerRevision': 'Pointer revision',
  'printForm.publishedAt': 'Published',
  'printForm.activatedAt': 'Activated',
  'printForm.publishedBy': 'Published by',
  'printForm.activatedBy': 'Activated by',
  'printForm.provider': 'Built by',
  'printForm.runtimeApi': 'Runtime API',
  'printForm.bundleSchema': 'Bundle schema',
  'printForm.bundleHash': 'Bundle hash',

  /*
   * Readiness is DERIVED from the two facts beside it — the form is in use and
   * a release is activated — rather than being a state the server publishes.
   * The reason it is on screen at all is that meeting neither condition looks
   * identical to a clinician: the print action is simply absent, with nothing
   * saying which of the two to fix.
   */
  'printForm.readiness': 'Printing',
  'printForm.readinessReady': 'Ready',
  'printForm.readinessNotReady': 'Not ready',

  'printForm.schemasTitle': 'Data schemas',
  'printForm.schemasEmpty': 'This clinic has no data schema published yet',
  'printForm.schemaName': 'Schema',
  'printForm.schemaVersionColumn': 'Version',
  'printForm.schemaCode': 'Code',
  'printForm.schemaDescription': 'Description',
  'printForm.schemaDigest': 'Semantic digest',
  'printForm.compatibilityTitle': 'Compatibility routes',
  'printForm.compatibilityEmpty': 'No compatible source routes are active',
  'printForm.compatibilityLanguage': 'Language',
  'printForm.compatibilitySource': 'Source contract',
  'printForm.compatibilityBuilder': 'Builder',
  'printForm.compatibilityLegacyIncomplete': '{count} legacy records have incomplete historical print context',
  'printForm.compatibilitySummary':
    '{sources} source versions, {builders} builders and {releases} releases are inventoried',
  'printForm.compatibilityIssuesTitle': 'Outstanding compatibility work',
  'printForm.compatibilityIssuesEmpty': 'No unsupported historical print contracts were found',
  'printForm.compatibilityIssueBuilder': '{identity}: no compatible context builder is registered ({count})',
  'printForm.compatibilityIssueRoute': '{identity}: drafts have no active source route ({count})',
  'printForm.compatibilityIssueHistory': '{identity}: historical print context is incomplete ({count})',
  'printForm.compatibilityIssueRelease': '{identity}: released context semantics are unavailable ({count})',

  'printForm.editorOpen': 'Open the print form editor',
  // The affordance the link itself cannot state: a top-level navigation to
  // another application on this origin, already signed in as this account.
  'printForm.editorOpensInNewTab': 'The editor opens in a new tab, signed in as this account.',
  'printForm.editorChecking': 'Checking the print form editor',
  'printForm.editorUnavailable': 'The print form editor is not answering',
  'printForm.editorUnavailableDetail':
    'It runs as a separate service. Nothing registered here is affected; try again once it is back.',

  // What this account would be in the editor, derived from the capabilities the
  // server issued this session — never from the address or anything the browser
  // stored.
  'printForm.editorPrivileges': 'In the editor you are: {privileges}',
  'printForm.privilegeEditor': 'an editor',
  'printForm.privilegePublisher': 'a publisher',
  "quotas.title": "Limits and usage",
  "quotas.description": "Organization limits and current usage.",
  "quotas.employees": "Employees",
  "quotas.branches": "Branches",
  "quotas.storage": "Storage",
  "quotas.storageGB": "Storage, GB",
  "quotas.gb": "{value} GB",
  "quotas.unlimited": "Unlimited",
  "quotas.used": "Used",
  "quotas.limit": "Limit",
  "quotas.reserved": "Reserved",
  "quotas.remaining": "Available",
  "quotas.stateOk": "Within limit",
  "quotas.stateTight": "Nearly full",
  "quotas.stateFull": "Limit reached",
  "quotas.stateOver": "Over limit",
  "quotas.usedPercent": "{percent}% used",
  "quotas.overLimit": "Usage exceeds the limit. Existing data is retained; additions are blocked.",
  "quotas.estimate": "Storage includes retained files and a stable estimate of database space. 1 GB = 1,000,000,000 bytes.",
  "quotas.readOnly": "Storage is full. Reading and downloading remain available. Free space or ask for a higher limit to resume changes.",
  "quotas.loadFailed": "Quotas could not be loaded.",
  "quotas.saveFailed": "Limits were not saved. Your changes remain in the form.",
  "quotas.saved": "Limits saved.",
  "quotas.save": "Save limits",
  "quotas.reload": "Reload limits and discard form changes",
  "quotas.reason": "Reason for the change",
  "quotas.conflict": "Limits changed on the server. Your changes remain here. Reload the current limits before trying again.",
  "quotas.invalid": "Enter valid limits: at least one branch, nonnegative employee and storage limits.",
  "quotas.unsaved": "Unsaved quota limits",
  "quotas.emptyUnlimited": "Leave a limit empty for unlimited access.",
  "quotas.exceeded": "The organization quota was reached. Your unsaved changes remain available.",
  'quotas.dismiss': 'Dismiss',
} as const
